import {
  Component,
  Input,
  OnInit,
  ChangeDetectorRef,
  OnDestroy,
  Output,
  EventEmitter,
} from '@angular/core';
import { buttonStatus } from '../../../../shared/constants/button-class';
import { Popover } from 'primeng/popover';
import { toggleEllipsisPopover } from 'src/app/modules/crm/shared/utilities/ellipsis-popover.util';
import {
  ellipsisOptions,
  ellipsisHeaderOptions,
} from 'src/app/modules/crm/shared/constants/ellipsis-options';
import { KeyMap } from '../../../../shared/interface/key-map.interface';
import { v4 as uuidv4 } from 'uuid';
import { ConfirmationService, MessageService } from 'primeng/api';
import { PriceTable } from '../../../../crm-coverage/coverage-models';
import { ServicersTableService } from './servicers-table.service';
import { ServiceOrderService } from '../../service-order.service';
import { ServiceOrderDataService } from '../../service-order-data.service';
import { Subscription } from 'rxjs';
import { AzureMapsService } from 'src/app/services/azure-maps.service';
import { copy } from 'src/app/modules/crm/shared/utilities/common-utilities';
import { Table } from 'primeng/table';
import {
  resolveSelectedColumns,
  saveSelectedColumns,
} from '../../../../shared/utilities/crm-table-utilities';

@Component({
  standalone: false,
  selector: 'servicers-table',
  templateUrl: 'servicers-table.template.html',
  styleUrls: ['./servicers-table.scss'],
  providers: [ServicersTableService, AzureMapsService, MessageService],
})
export class ServicersTableComponent implements OnInit, OnDestroy {
  @Output() selectedEvent: EventEmitter<boolean> = new EventEmitter();
  @Output() dispatchModelEvent: EventEmitter<boolean> = new EventEmitter();
  dataList = [];
  cols: any[];
  btnStatus: any = buttonStatus;
  selectedItem: any;
  ellipsisOptions: any[] = ellipsisOptions;
  ellipsisHeaderOptions: any[] = copy(ellipsisHeaderOptions);
  selectedEllipsisItem: any;
  clonedRowData: KeyMap = {};
  isEditing: boolean;
  editConstant: string;
  selectedRowIndex: number;
  selectedColumns: any[];
  private readonly tableColumnId = 'servicers-table';
  querySubscriptions: Subscription[] = [];
  selectedServicer: any;
  isLoading: boolean;
  selectedState: any;
  latitude: any;
  longitude: any;
  offset = 0;
  limit = 500;
  displayOnInvalidAddress: boolean = false;
  suggestedAddress: string;

  constructor(
    private servicersTableService: ServicersTableService,
    private confirmationService: ConfirmationService,
    private serviceOrderService: ServiceOrderService,
    private serviceOrderDataService: ServiceOrderDataService,
    private cdr: ChangeDetectorRef,
    private azureMaps: AzureMapsService,
    private messageService: MessageService,
  ) {}

  ngOnInit() {
    this.editConstant = uuidv4();
    const { location } = this.serviceOrderDataService.serviceOrderDataModel;
    this.selectedState = location && location.state && location.state.value;
    if (this.selectedState) {
      const address1 = location.address1;
      const city = location.city;
      const state = location.state.value;
      const zipCode = location.zipCode;
      const addressQueryStr = `${address1} ${city} ${state} ${zipCode}`;
      if (address1 && city && state && zipCode && address1 !== 'undefined') {
        this.isLoading = true;
        this.querySubscriptions.push(
          this.azureMaps.getAddressDetails(addressQueryStr).subscribe(
            (address) => {
              this.serviceOrderDataService.azureMapAddressInfo = {
                address1: address.address1,
                city: address.city,
                latitude: address.latitude,
                longitude: address.longitude,
                state: address.state,
                suggestedFullAddress: address.suggestedFullAddress,
                zipCode: address.zipCode,
              };
              this.latitude = address.latitude;
              this.longitude = address.longitude;
              this.suggestedAddress = address.suggestedFullAddress;
              if (address.zipCode === zipCode) {
                this.getEligibleServicerDetails();
              } else {
                // popup the alert to confirm to proceed with dispatch if the address is invalid
                this.displayOnInvalidAddress = true;
              }
            },
            () => {
              this.isLoading = false;
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Please enter a valid address before proceeding with Dispatch',
              });
            },
          ),
        );
      }
    }
    this.cols = [
      { field: 'select', header: 'SELECT', type: 'radio' },
      { field: 'servicerName', header: 'SERVICER NAME', type: 'text' },
      { field: 'selfServicing', header: 'SELF SERVICING', type: 'checkbox' },
      { field: 'servicerStatus', header: 'SERVICER STATUS', type: 'text' },
      { field: 'phoneNumber', header: 'PHONE NUMBER', type: 'phone' },
      { field: 'servicerGroup', header: 'SERVICER GROUP', type: 'text' },
      { field: 'distance', header: 'DISTANCE', type: 'text' },
      { field: 'sealedSystemsRepair', header: 'SEALED SYSTEMS', type: 'checkbox' },
      { field: 'isMFGAuthorized', header: 'MFG AUTHORIZED', type: 'checkbox' },
      { field: 'rating', header: 'RATING', type: 'text' },
      { field: 'customerSatisfaction', header: 'CUSTOMER SATISFACTION', type: 'text' },
      { field: 'validInsurance', header: 'DEFAULT PROFILE', type: 'checkbox' },
      { field: 'facilityType', header: 'FACILITY TYPE', type: 'text' },
      { field: 'contracted', header: 'CONTRACTED', type: 'checkbox' },
    ];

    // Take original 10 columns first, then filter out phoneNumber (keeping original defaults)
    this.selectedColumns = resolveSelectedColumns(
      this.tableColumnId,
      this.cols,
      this.cols.slice(0, 10).filter((col) => col.field !== 'phoneNumber'),
    );
  }

  formatPhoneNumber(phone: string): string {
    if (!phone) {
      return '';
    }

    const cleaned = phone.replace(/\D/g, '');
    // Format as (XXX) XXX – XXXX
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)} – ${cleaned.slice(6)}`;
    }
    return phone;
  }

  onColumnChange() {
    // Sort selectedColumns based on the original order in cols
    this.selectedColumns = this.selectedColumns.sort((a, b) => {
      return this.cols.findIndex(col => col.field === a.field) - this.cols.findIndex(col => col.field === b.field);
    });
    saveSelectedColumns(this.tableColumnId, this.selectedColumns);
  }

  filter(table: Table, $event: any) {
    table.filterGlobal($event.target.value, 'contains')
  }

  ellipsisClick(event, item, Popover: Popover, rowIndex) {
    this.selectedItem = item;
    this.selectedRowIndex = rowIndex;
    toggleEllipsisPopover(event, Popover);
  }

  ellipsisOptionClick(event) {
    // ellipsis functionality goes here
  }

  onRowEdit() {
    this.isEditing = true;
    document.getElementById(this.editConstant + this.selectedItem.id).click();
  }

  onRowEditInit(rowData: any) {
    this.clonedRowData[rowData.id] = { ...rowData };
  }
  onRowEditSave(rowData: any) {
    delete this.clonedRowData[rowData.id];
    this.isEditing = false;
  }

  onRowEditCancel(rowData: any, index: number) {
    if (rowData[`isNew`]) {
      this.confirmationService.confirm({
        message: 'Are you sure that you want to discard, these changes?',
        accept: () => {
          this.dataList[index] = this.clonedRowData[rowData.id];
          delete this.clonedRowData[rowData.id];
          this.isEditing = false;
          this.dataList.splice(this.selectedRowIndex, 1);
        },
      });
    } else {
      this.dataList[index] = this.clonedRowData[rowData.id];
      delete this.clonedRowData[rowData.id];
      this.isEditing = false;
    }
  }

  onClickAdd(event) {
    let rowItem = new PriceTable();
    rowItem.id = this.dataList.length + 1;
    rowItem[`isNew`] = true;
    rowItem = { ...rowItem };
    this.dataList.push(rowItem);
    this.cdr.detectChanges();
    this.selectedItem = rowItem;
    this.selectedRowIndex = this.dataList.length - 1;
    this.onRowEdit();
  }

  onRowDelete(event, Popover: Popover) {
    Popover.hide();
    this.confirmationService.confirm({
      message: 'Are you sure that you want to delete this row?',
      accept: () => {
        this.dataList.splice(this.selectedRowIndex, 1);
      },
    });
  }

  onRadioButtonClick(event, rowData) {
    this.serviceOrderService.selectedServicerValue = rowData;
    this.selectedEvent.emit(true);
  }

  OnConfirmToDispatch() {
    this.displayOnInvalidAddress = false;
    this.getEligibleServicerDetails();
  }

  OnCancelToDispatch() {
    this.displayOnInvalidAddress = false;
    this.dispatchModelEvent.emit(this.displayOnInvalidAddress);
  }

  getEligibleServicerDetails() {
    const queryParams = {
      state: this.selectedState,
      latitude: this.latitude,
      longitude: this.longitude,
      serviceType:
        this.serviceOrderDataService.serviceOrderDataModel.claimData &&
        this.serviceOrderDataService.serviceOrderDataModel.claimData.serviceType
          ? this.serviceOrderDataService.serviceOrderDataModel.claimData.serviceType.value
          : null,
      city: this.serviceOrderDataService.serviceOrderDataModel.location
        ? this.serviceOrderDataService.serviceOrderDataModel.location.city
        : null,
      zipCode: this.serviceOrderDataService.serviceOrderDataModel.location
        ? this.serviceOrderDataService.serviceOrderDataModel.location.zipCode
        : null,
      manufacturerName: this.serviceOrderDataService.serviceOrderDataModel.productInfoModel
        ? this.serviceOrderDataService.serviceOrderDataModel.productInfoModel
            .manufacturerManufacturer
        : null,
      modelName: this.serviceOrderDataService.serviceOrderDataModel.productInfoModel
        ? this.serviceOrderDataService.serviceOrderDataModel.productInfoModel.modelNumber
        : null,
      contractNumber: this.serviceOrderDataService.serviceOrderDataModel.contractInfo
        ? this.serviceOrderDataService.serviceOrderDataModel.contractInfo.contractNumber
        : null,
      productId: this.serviceOrderDataService.serviceOrderDataModel.productInfoModel
        ? this.serviceOrderDataService.serviceOrderDataModel.productInfoModel.productsIdOriginal
        : null,
      limit: this.limit,
      offset: this.offset,
    };
    this.isLoading = true;
    this.querySubscriptions.push(
      this.servicersTableService.getServiceDetails(queryParams).subscribe(
        ({ data, loading }: any) => {
          if (data.getServicerEligibleProfiles && data.getServicerEligibleProfiles.length) {
            const responseRows = [];
            for (const it of data.getServicerEligibleProfiles) {
              const singleRow = Object.assign({},it);
              let servicerStatus = '';
              let populated = false;
              if (singleRow.preferred === 1) {
                servicerStatus += 'Preferred';
                populated = true;
              }
              if (singleRow.restricted === 1) {
                if (populated) {
                  servicerStatus += '\n';
                }
                servicerStatus += 'Restricted';
              }
              if (servicerStatus) {
                servicerStatus += ', ' + singleRow.servicerStatus;
              } else {
                servicerStatus = singleRow.servicerStatus;
              }
              singleRow.servicerStatus = servicerStatus;
              responseRows.push(singleRow);
            }
            this.dataList = [...this.dataList, ...responseRows];
            this.offset = this.offset + this.limit;
          }
          this.isLoading = loading;
        },
        (err) => {
          this.isLoading = false;
          throw err;
        },
      ),
    );
  }

  ngOnDestroy() {
    this.querySubscriptions.forEach((subs) => subs.unsubscribe());
  }
}
